import React, {useState} from 'react';
import {StyleSheet} from 'react-native';
import Metrics from 'design-system/src/Theme/Metrics';
import SocialItemButton from './SocialItemButton/SocialItemButton';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import useFireBaseAuthentication, {
  IFirebaseAuthenticationResponse,
} from '@src/Hooks/useFirebaseAuthentication';
import appAuthSlice, {
  SocialNetworkSource,
} from '@src/Store/Slices/AppSlice/auth.slice';
import {useAppDispatch} from '@src/Store/hooks';
import {toast} from 'react-toastify';
import appSlice from '@src/Store/Slices/AppSlice';
import useTracker from '@src/Services/Amplitude/hooks/useTracker';
import {trackTealiumLoginSuccessful} from '@src/Tracking/tealiumEvents';
import apiConfig from '@src/Configs/apiConfig';
import {getAllowedAuthProviders} from '@src/Configs/authDomainConfig';
import VodacomOtpLogin from '../../VodacomOtpLogin/VodacomOtpLogin';
import MtnOtpLogin from '../../MtnOtpLogin/MtnOtpLogin';

interface ISocialNetworkAuthenticationProps {
  isSignIn?: boolean;
  onSuccess?: () => void;
}

const SocialNetworkAuthentication = (
  props: ISocialNetworkAuthenticationProps,
) => {
  const {isSignIn, onSuccess} = props;
  const {t} = useTranslation('auth');
  const tracker = useTracker();
  const [loadingItem, setLoadingItem] = useState<SocialNetworkSource | null>(
    null,
  );
  const {signInWithGoogle, signInWithFacebook, signInWithApple} =
    useFireBaseAuthentication();
  const dispatch = useAppDispatch();
  const allowedProviders = getAllowedAuthProviders();
  const vodacomOnly =
    allowedProviders.length === 1 && allowedProviders[0] === 'vodacom';
  const [showVodacomOtp, setShowVodacomOtp] = useState(vodacomOnly);
  const mtnOnly =
    allowedProviders.length === 1 && allowedProviders[0] === 'mtn';
  const [showMtnOtp, setShowMtnOtp] = useState(mtnOnly);

  const buttonLabel: Record<SocialNetworkSource, string> = {
    apple: isSignIn ? t('Sign in WITH apple') : t('Register with apple'),
    facebook: isSignIn
      ? t('Sign in WITH facebook')
      : t('Register with facebook'),
    google: isSignIn ? t('Sign in WITH google') : t('Register with google'),
    email: isSignIn ? t('Sign in with my Email') : t('Register with my Email'),
    vodacom: isSignIn
      ? t('Sign in with Vodacom')
      : t('Join as Free User (Mobile)'),
    mtn: isSignIn ? t('Sign in with MTN') : t('Join with MTN'),
  };

  const handleOnClick = async (source: SocialNetworkSource) => {
    setLoadingItem(source);
    dispatch(appAuthSlice.actions.setSocialNetworkSource(source));
    dispatch(appSlice.actions.setAccountType(source));

    let response: IFirebaseAuthenticationResponse | null = null;

    switch (source) {
      case 'apple':
        response = await signInWithApple(!isSignIn);
        break;
      case 'facebook':
        response = await signInWithFacebook(!isSignIn);
        break;
      case 'google':
        response = await signInWithGoogle(!isSignIn);
        break;
      case 'vodacom':
        setShowVodacomOtp(true);
        setLoadingItem(null);
        return;
      case 'mtn':
        setShowMtnOtp(true);
        setLoadingItem(null);
        return;
      case 'email':
        break;
    }

    const processMember = async () => {
      tracker.general.loggedIn({account_type: source});
      trackTealiumLoginSuccessful({accountType: source});
      dispatch(appAuthSlice.actions.closeAuthModal());
    };

    if (isSignIn) {
      if (response && response.status == 'success') {
        processMember();
        onSuccess?.();
      } else if (response && response.status == 'error') {
        toast.error(response.errorMessage);
      }
      // Si pas de response (popup fermé par l'utilisateur), ne rien faire
    } else if (response) {
      if (response.status == 'success') {
        const splittedDisplayName: string[] =
          response.user?.displayName?.split(' ') || [];
        dispatch(
          appAuthSlice.actions.setFormData({
            email: response.user?.email || '',
            lastName: splittedDisplayName.pop() || '',
            firstName: splittedDisplayName.join(' '),
          }),
        );
        dispatch(appAuthSlice.actions.setAuthStep({page: 'form', step: 0}));
      } else {
        console.log('response.errorMessage', response.errorMessage);
        toast.error(response.errorMessage);
      }
    }
    setLoadingItem(null);
  };

  if (showVodacomOtp) {
    return (
      <VodacomOtpLogin
        compact
        onBack={vodacomOnly ? undefined : () => setShowVodacomOtp(false)}
        onSuccess={onSuccess}
      />
    );
  }

  if (showMtnOtp) {
    return (
      <MtnOtpLogin
        compact
        onBack={mtnOnly ? undefined : () => setShowMtnOtp(false)}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <div style={{width: '100%'}}>
      {apiConfig.withFacebookConnect && allowedProviders.includes('facebook') && (
        <SocialItemButton
          style={SocialNetworkButtonStyles.button}
          title={buttonLabel.facebook}
          icon={'facebook'}
          onPress={() => {
            handleOnClick('facebook');
          }}
          loading={loadingItem === 'facebook'}
        />
      )}
      {allowedProviders.includes('apple') && (
        <SocialItemButton
          style={SocialNetworkButtonStyles.button}
          title={buttonLabel.apple}
          icon={'apple'}
          onPress={() => {
            handleOnClick('apple');
          }}
          loading={loadingItem === 'apple'}
        />
      )}
      {allowedProviders.includes('google') && (
        <SocialItemButton
          style={SocialNetworkButtonStyles.button}
          title={buttonLabel.google}
          icon={'googleG'}
          onPress={() => {
            handleOnClick('google');
          }}
          loading={loadingItem === 'google'}
        />
      )}
      {allowedProviders.includes('vodacom') && (
        <SocialItemButton
          style={SocialNetworkButtonStyles.button}
          title={buttonLabel.vodacom}
          icon={'vodacom'}
          onPress={() => {
            handleOnClick('vodacom');
          }}
          loading={loadingItem === 'vodacom'}
        />
      )}
      {allowedProviders.includes('mtn') && (
        <SocialItemButton
          style={SocialNetworkButtonStyles.button}
          title={buttonLabel.mtn}
          onPress={() => {
            handleOnClick('mtn');
          }}
          loading={loadingItem === 'mtn'}
        />
      )}
    </div>
  );
};

const SocialNetworkButtonStyles = StyleSheet.create({
  title: {
    marginBottom: Metrics.horizontalScale(28),
  },
  button: {
    marginBottom: Metrics.horizontalScale(16),
  },
  emailButton: {
    marginTop: Metrics.horizontalScale(20),
    height: Metrics.verticalScale(48),
    justifyContent: 'center',
  },
});

export default SocialNetworkAuthentication;
